import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStoredTokens, clearTokens, decodeJWT } from '../utils/auth';
import { useLanguage } from '../context/LanguageContext';
import './Navbar.css';

const LOGO_URL = process.env.PUBLIC_URL + "/adflynLogo.png";

const AVAILABLE_LANGUAGES = [
  { code: 'en-UK', label: '🇬🇧 English UK' },
  { code: 'en-US', label: '🇺🇸 English US' },
  { code: 'kn', label: '🇮🇳 Kannada' },
  { code: 'ta', label: '🇮🇳 Tamil' },
  { code: 'hi', label: '🇮🇳 Hindi' },
  { code: 'ml', label: '🇮🇳 Malayalam' },
  { code: 'te', label: '🇮🇳 Telugu' }
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const profileMenuRef = useRef(null);
  const languageMenuRef = useRef(null);

  useEffect(() => {
    // Get user info from token
    const tokens = getStoredTokens();
    if (tokens?.idToken) {
      try {
        const decoded = decodeJWT(tokens.idToken);
        setUserInfo({
          name: decoded.name || decoded.email?.split('@')[0] || 'User',
          email: decoded.email || ''
        });
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Close profile menu when clicking outside
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearTokens();
    navigate('/');
  };

  const navItems = [
    { name: t('navbar.adflyn_chatbot'), path: '/adflyn-chatbot' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'UGC Videos', path: '/ugc-videos' },
    { name: t('navbar.social_media'), path: '/social-media-automation' },
    { name: t('navbar.calendar'), path: '/calendar' },
    { name: 'Analytics', path: '/analytics' }
  ];

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <button
          className="navbar-logo"
          onClick={() => navigate('/adflyn-chatbot')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/adflyn-chatbot');
            }
          }}
          aria-label="Navigate to home"
        >
          <img src={LOGO_URL} alt="Adflyn Logo" />
        </button>

        {/* Navigation Links */}
        <div className="navbar-links">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text">{item.name}</span>
            </button>
          ))}
        </div>

        {/* Language Dropdown */}
        <div className="navbar-language" ref={languageMenuRef}>
          <button
            className="language-button"
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            title="Select Language"
            aria-label="Change language"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M2 12h20"></path>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            <span className="language-label">
              {AVAILABLE_LANGUAGES.find(l => l.code === language)?.label.split(' ')[1] || 'EN'}
            </span>
          </button>

          {showLanguageMenu && (
            <div className="language-menu">
              {AVAILABLE_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-menu-item ${language === lang.code ? 'active' : ''}`}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setShowLanguageMenu(false);
                  }}
                >
                  <span>{lang.label}</span>
                  {language === lang.code && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Profile Section */}
        <div className="navbar-profile" ref={profileMenuRef}>
          <button
            className="profile-button"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            <div className="profile-avatar">
              {getInitials(userInfo.name)}
            </div>
            <span className="profile-name">{userInfo.name}</span>
            <svg
              className={`profile-arrow ${showProfileMenu ? 'open' : ''}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {showProfileMenu && (
            <div className="profile-menu">
              <div className="profile-menu-header">
                <div className="profile-avatar-large">
                  {getInitials(userInfo.name)}
                </div>
                <div className="profile-info">
                  <div className="profile-menu-name">{userInfo.name}</div>
                  <div className="profile-menu-email">{userInfo.email}</div>
                </div>
              </div>
              <div className="profile-menu-divider"></div>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/profile');
              }}>
                <span>👤</span>
                <span>{t('profile.my_profile')}</span>
              </button>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/settings');
              }}>
                <span>⚙️</span>
                <span>{t('profile.settings')}</span>
              </button>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/business-dna');
              }}>
                <span>🧬</span>
                <span>{t('profile.business_dna')}</span>
              </button>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/settings/general');
              }}>
                <span>📋</span>
                <span>{t('profile.general')}</span>
              </button>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/settings/preferences');
              }}>
                <span>🎚</span>
                <span>{t('profile.preferences')}</span>
              </button>
              <button className="profile-menu-item" onClick={() => {
                setShowProfileMenu(false);
                navigate('/settings/billing');
              }}>
                <span>💳</span>
                <span>{t('profile.billing')}</span>
              </button>
              <div className="profile-menu-divider"></div>
              <button className="profile-menu-item logout" onClick={handleLogout}>
                <span>🚪</span>
                <span>{t('profile.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
